import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DefaultTheme } from 'styled-components';
import * as S from './styles';
import {
  faSave,
  faUsers,
  faCheck,
  faEraser,
} from '@fortawesome/free-solid-svg-icons';
import { FormData, Usuario } from './types';
import { Modal } from '../../../components/interface/Modal';
import Table from '../../../components/interface/Tabela';
import { Column } from '../../../components/interface/Tabela/types';
import FormInput from '../../../components/interface/FormInput';
import FormSelect from '../../../components/interface/FormSelect';
import FormTextArea from '../../../components/interface/FormTextArea';
import InputSearchCodigo from '../../../components/interface/InputSearchCodigo';
import ActionButtons from '../../../components/interface/ActionButtons';
import { ActionButtonProps } from '../../../components/interface/ActionButtons/types';

interface UsuarioProps {
  formData: FormData;
  error: string;
  success: string;
  searchTerm: string;
  usuarios: Usuario[];
  handleChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleSearch: () => void;
  handleSearchInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleEdit: (usuario: Usuario) => void;
  setUsuarios: React.Dispatch<React.SetStateAction<Usuario[]>>;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  setSuccess: React.Dispatch<React.SetStateAction<string>>;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
}

const UsuarioContent: React.FC<UsuarioProps> = ({
  formData,
  error,
  success,
  searchTerm,
  usuarios,
  handleChange,
  handleSubmit,
  handleSearch,
  handleSearchInputChange,
  handleEdit,
  setUsuarios,
  setSearchTerm,
  setSuccess,
  setFormData,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (usuarios.length === 1) {
      handleEdit(usuarios[0]);
    } else if (usuarios.length > 1) {
      setIsModalOpen(true);
    }
  }, [handleEdit, usuarios]);

  const handleCloseModal = useCallback(() => {
    setUsuarios([]);
    setSuccess('');
    setIsModalOpen(false);
  }, [setUsuarios, setSuccess]);

  const handleSelectUsuario = useCallback((usuario: Usuario) => {
    handleEdit(usuario);
    setIsModalOpen(false);
  }, [handleEdit]);

  const handleClearSearch = useCallback(() => {
    setUsuarios([]);
    setSearchTerm('');
    setSuccess('');
    setIsModalOpen(false);

    const empresa = formData.empresa;
    setFormData({
      nomeUsuario: '',
      cpf: '',
      telefone: '',
      email: '',
      observacao: '',
      login: '',
      codigoGrupoUsuario: 2,
      empresa,
    });
  }, [setUsuarios, setSearchTerm, setSuccess, setFormData, formData.empresa]);

  const handleSearchOnBlur = useCallback(() => {
    if (searchTerm.trim() !== '') {
      handleSearch();
    }
  }, [searchTerm, handleSearch]);

  const columns = useMemo<Column<Usuario>[]>(() => [
    {
      key: 'codigoUsuario',
      header: 'Código',
      width: '100px',
      filterable: true,
    },
    {
      key: 'nomeUsuario',
      header: 'Nome',
      filterable: true,
    },
    {
      key: 'cpf',
      header: 'CPF',
      filterable: true,
    },
    {
      key: 'email',
      header: 'Email',
      filterable: true,
    },
    {
      key: 'grupoUsuario',
      header: 'Grupo',
      filterable: true,
      render: (usuario) => usuario.grupoUsuario?.nome || '',
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
    },
  ], []);

  const actions = useMemo(() => [
    {
      icon: faCheck,
      onClick: handleSelectUsuario,
      title: 'Selecionar',
    },
  ], [handleSelectUsuario]);

  const grupoUsuarioOptions = [
    { value: 2, label: 'Entregador' },
    { value: 3, label: 'Administrador' }
  ];

  const actionButtons = useMemo<ActionButtonProps[]>(() => [
    {
      icon: faSave,
      title: 'Salvar',
      type: 'submit',
      backgroundColor: (theme: DefaultTheme) => theme.primaryColor
    },
    {
      icon: faEraser,
      title: 'Limpar formulário',
      onClick: handleClearSearch,
      backgroundColor: '#4dc9c9',
    }
  ], [handleClearSearch]);

  return (
    <S.CadastrosContainer>
      <S.CadastrosContent>
        <S.FormContainer>
          <div style={{ display: 'flex', justifyContent: 'start',marginLeft: '3.7em'}}>
          <InputSearchCodigo
            label="Código:"
            placeholder="Buscar usuário por código"
            value={searchTerm}
            onChange={handleSearchInputChange}
            onSearch={handleSearch}
            onBlur={handleSearchOnBlur}
            width="400px"
          />
          </div>

          {error && <S.ErrorMessage>{error}</S.ErrorMessage>}
          {success && <S.SuccessMessage>{success}</S.SuccessMessage>}

          {/* Modal de seleção de usuários */}
          <Modal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            title={`Selecionar Usuário (${usuarios.length})`}
            headerIcon={faUsers}
            size="large"
            noBodyScroll
          >
            <Table
              data={usuarios}
              columns={columns}
              actions={actions}
              exportable={true}
            />
          </Modal>

          <S.Form onSubmit={handleSubmit}>
            <>
              <S.FormRow>
                  <FormInput
                    label="Nome:"
                    name="nomeUsuario"
                    id="nomeUsuario"
                    value={formData.nomeUsuario}
                    onChange={handleChange}
                    required
                    labelPosition="left"
                    fullWidth
                  />

                  <FormInput
                    label="CPF:"
                    name="cpf"
                    id="cpf"
                    value={formData.cpf}
                    onChange={handleChange}
                    placeholder="000.000.000-00"
                    required
                    labelPosition="left"
                    fullWidth
                  />
              </S.FormRow>

              <S.FormRow>
                  <FormInput
                    label="E-mail:"
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    labelPosition="left"
                    fullWidth
                  />

                  <FormSelect
                    label="Grupo de Usuário:"
                    name="codigoGrupoUsuario"
                    id="codigoGrupoUsuario"
                    value={formData.codigoGrupoUsuario}
                    onChange={handleChange}
                    labelPosition="left"
                    fullWidth
                    options={grupoUsuarioOptions}
                  />
              </S.FormRow>

              <S.FormRow>
                  <FormInput
                    label="Telefone:"
                    type="text"
                    name="telefone"
                    id="telefone"
                    value={formData.telefone}
                    onChange={handleChange}
                    placeholder="(00) 00000-0000"
                    labelPosition="left"
                    fullWidth
                  />
              </S.FormRow>

              <S.FormRow>
                <FormTextArea
                  label="Observação:"
                  name="observacao"
                  id="observacao"
                  value={formData.observacao}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  fullWidth
                />
              </S.FormRow>

              <S.FormRow
                style={{ justifyContent: 'center', marginTop: '20px' }}
              >
                <ActionButtons
                  buttons={actionButtons}
                />
              </S.FormRow>
            </>
          </S.Form>
        </S.FormContainer>
      </S.CadastrosContent>
    </S.CadastrosContainer>
  );
};

export default UsuarioContent;
